import { Component, ElementRef, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IContractVaultArtifact, IContractVaultSummary } from 'src/app/shared/models/v1/contract-vault/contract-vault.model';
import { ContractVaultHttpService } from 'src/app/shared/services/v1/contract-vault/contract-vault.http.service';
import { openFilePreviewDialog } from 'src/app/shared/components/file-preview-dialog/file-preview-dialog.composable';
import { openShareLinkDialog } from 'src/app/shared/components/share-link-dialog/share-link-dialog.composable';
import { apiUrl } from 'src/environments/environment';


/**
 * Dialog input contract for opening the Contract Vault modal.
 */
export interface IContractVaultDialogData {
  proposalProfileId: string;
  proposalName?: string;
  contractAppEmbedUrl?: string;
  initialTabIndex?: number;
}

/**
 * Robust Contract Vault Hub Dialog:
 * - Tab 0: Live Interactive Contract App (Edge-to-edge embedded iframe with Keepalive, loading state, and error handling)
 * - Tab 1: Archived Artifacts & Download Vault (One-click downloads for PDF, W-9, DOCX)
 */
@Component({
  selector: 'app-contract-vault-dialog',
  templateUrl: './contract-vault-dialog.component.html',
  styleUrls: ['./contract-vault-dialog.component.scss']
})
export class ContractVaultDialogComponent implements OnInit, OnDestroy {
  @ViewChild('contractIframe') public iframeElement?: ElementRef<HTMLIFrameElement>;

  public proposalProfileId: string = '';
  public proposalName: string = '';
  public activeTabIndex: number = 0;

  // Granular State Management
  public summaryLoading: boolean = true;
  public artifactsLoading: boolean = false;
  public iframeLoading: boolean = true;
  public iframeError: boolean = false;
  public errorMessage: string | null = null;

  public summary: IContractVaultSummary | null = null;
  public rawEmbedUrl: string = '';
  public safeEmbedUrl: SafeResourceUrl | null = null;

  private iframeWatchdogTimer: any = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: IContractVaultDialogData,
    @Optional() private dialogRef: MatDialogRef<ContractVaultDialogComponent>,
    private vaultHttpService: ContractVaultHttpService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog
  ) {
    if (data) {
      this.proposalProfileId = data.proposalProfileId || '';
      this.proposalName = data.proposalName || 'Proposal';
      this.activeTabIndex = data.initialTabIndex || 0;
      if (data.contractAppEmbedUrl) {
        this.rawEmbedUrl = data.contractAppEmbedUrl;
        this.safeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(data.contractAppEmbedUrl);
      }
    }
  }


  ngOnInit(): void {
    this.loadVaultSummary();
  }

  /**
   * Switches the active tab index (0: Interactive Workspace, 1: Archived Artifacts).
   * @param index Target tab index.
   */
  public selectTab(index: number): void {
    this.activeTabIndex = index;
  }

  ngOnDestroy(): void {

    if (this.iframeWatchdogTimer) {
      clearTimeout(this.iframeWatchdogTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads the contract execution snapshot and list of file artifacts from BOF backend.
   */
  public loadVaultSummary(forceIframeReload: boolean = false): void {
    if (!this.proposalProfileId) {
      this.errorMessage = 'Proposal ID is missing.';
      this.summaryLoading = false;
      return;
    }

    this.summaryLoading = true;
    this.errorMessage = null;

    this.vaultHttpService.getSummary(this.proposalProfileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          this.summaryLoading = false;
          if (resp && resp.result) {
            this.summary = resp.result;

            const resolvedUrl = this.data?.contractAppEmbedUrl || this.summary.contractAppEmbedUrl;
            if (resolvedUrl && (resolvedUrl !== this.rawEmbedUrl || forceIframeReload || !this.safeEmbedUrl)) {
              this.rawEmbedUrl = resolvedUrl;
              this.iframeLoading = true;
              this.iframeError = false;
              this.safeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(resolvedUrl);
              this.startIframeWatchdog();
            }
          }
        },
        error: (err) => {
          this.summaryLoading = false;
          this.errorMessage = err?.message || 'Failed to load contract vault summary.';
        }
      });
  }

  /**
   * Handles successful iframe DOM load event.
   */
  public onIframeLoad(): void {
    this.iframeLoading = false;
    this.iframeError = false;
    if (this.iframeWatchdogTimer) {
      clearTimeout(this.iframeWatchdogTimer);
    }
  }

  /**
   * Handles iframe failure / network block event.
   */
  public onIframeError(): void {
    this.iframeLoading = false;
    this.iframeError = true;
  }

  /**
   * Safety watchdog: if cross-origin iframe does not trigger load event within 5s, clears spinner.
   */
  private startIframeWatchdog(): void {
    if (this.iframeWatchdogTimer) {
      clearTimeout(this.iframeWatchdogTimer);
    }
    this.iframeWatchdogTimer = setTimeout(() => {
      if (this.iframeLoading) {
        this.iframeLoading = false;
      }
    }, 5000);
  }

  /**
   * Resets and reloads the Iframe back to the original setup URL.
   */
  public reloadOriginalUrl(): void {
    if (this.rawEmbedUrl) {
      this.iframeLoading = true;
      this.iframeError = false;
      this.safeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.rawEmbedUrl);
      this.startIframeWatchdog();
      if (this.iframeElement?.nativeElement) {
        this.iframeElement.nativeElement.src = this.rawEmbedUrl;
      }
    }
  }

  /**
   * Reloads the current iframe page, preserving navigation context.
   */
  public reloadCurrentIframe(): void {
    this.iframeLoading = true;
    this.iframeError = false;
    this.startIframeWatchdog();

    try {
      if (this.iframeElement?.nativeElement?.contentWindow) {
        this.iframeElement.nativeElement.contentWindow.location.reload();
        return;
      }
    } catch (e) {
      // Cross-origin restriction fallback
    }

    if (this.iframeElement?.nativeElement) {
      const currentSrc = this.iframeElement.nativeElement.src;
      this.iframeElement.nativeElement.src = currentSrc;
    }
  }

  /**
   * Refreshes the artifacts list from the vault summary without re-rendering or reloading the Contract App Iframe.
   */
  public reloadArtifactsOnly(): void {
    if (!this.proposalProfileId) return;

    this.artifactsLoading = true;
    this.vaultHttpService.getSummary(this.proposalProfileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          this.artifactsLoading = false;
          if (resp && resp.result) {
            this.summary = resp.result;
          }
        },
        error: (err) => {
          this.artifactsLoading = false;
          this.errorMessage = err?.message || 'Failed to refresh artifacts list.';
        }
      });
  }

  /**
   * Returns appropriate Material icon name for artifact filename.
   */
  public getFileIcon(fileName?: string): string {
    if (!fileName) return 'insert_drive_file';
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.pdf')) return 'picture_as_pdf';
    if (lower.endsWith('.docx') || lower.endsWith('.doc')) return 'description';
    if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.webp')) return 'image';
    return 'insert_drive_file';
  }

  /**
   * Returns CSS class for styling artifact icons in the table.
   */
  public getFileIconClass(fileName?: string): string {
    if (!fileName) return 'generic-file-icon';
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.pdf')) return 'pdf-file-icon';
    if (lower.endsWith('.docx') || lower.endsWith('.doc')) return 'docx-file-icon';
    if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.webp')) return 'image-file-icon';
    return 'generic-file-icon';
  }

  /**
   * Opens the current Contract App URL directly in a new browser tab.
   */
  public openInNewTab(): void {
    if (this.rawEmbedUrl) {
      window.open(this.rawEmbedUrl, '_blank');
    }
  }

  /**
   * Opens the full-screen document previewer dialog for the selected artifact.
   * @param artifact Target document artifact.
   */
  public previewArtifact(artifact: IContractVaultArtifact): void {
    if (artifact) {
      openFilePreviewDialog(this.dialog, {
        artifactId: artifact.id,
        viewUrl: artifact.viewUrl,
        downloadUrl: artifact.downloadUrl,
        fileName: artifact.fileName,
        fileSizeBytes: artifact.fileSizeBytes,
        category: this.getArtifactTypeLabel(artifact.artifactType)
      });
    }
  }


  /**
   * Opens the share link configuration dialog for an artifact.
   * @param artifact Target document artifact.
   */
  public shareArtifact(artifact: IContractVaultArtifact): void {
    if (artifact && artifact.id) {
      openShareLinkDialog(this.dialog, {
        artifactId: artifact.id,
        fileName: artifact.fileName,
        category: this.getArtifactTypeLabel(artifact.artifactType)
      });
    }
  }

  /**
   * Triggers direct file attachment download via authenticated HTTP client.
   * @param artifact Target document artifact.
   */
  public downloadArtifact(artifact: IContractVaultArtifact): void {
    if (artifact && artifact.downloadUrl) {
      this.vaultHttpService.downloadFileBlob(artifact.downloadUrl).subscribe({
        next: (blob) => {
          this.vaultHttpService.saveBlobAsFile(blob, artifact.fileName || 'document.pdf');
        },
        error: () => {
          // Fallback to direct tab opening if needed
          window.open(this.resolveAbsoluteUrl(artifact.downloadUrl), '_blank');
        }
      });
    }
  }


  /**
   * Resolves relative path against host API URL.
   */
  private resolveAbsoluteUrl(url?: string): string {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) {
      const host = apiUrl.replace(/\/api(\/v\d+)?\/?$/i, '');
      return `${host}${url}`;
    }
    return url;
  }





  /**
   * Formats raw bytes to human-readable size string.
   */
  public formatFileSize(bytes?: number): string {
    if (!bytes || bytes === 0) return '—';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Returns human-friendly badge label for artifact type.
   */
  public getArtifactTypeLabel(type: string): string {
    switch (type) {
      case 'CLIENT_CONTRACT': return 'Client Contract';
      case 'ARTIST_RIDER': return 'Artist Rider';
      case 'MSA': return 'Master Agreement (MSA)';
      case 'TAX_W9': return 'Tax Form (W-9)';
      case 'TAX_W8BEN': return 'Tax Form (W-8BEN)';
      case 'DOCX_EXPORT': return 'Word DOCX';
      case 'AUDIT_TRAIL': return 'Audit Certificate';
      default: return type || 'Document';
    }
  }

  // Retry States
  public retryingArtifactIds: Set<number> = new Set<number>();
  public retryingAll: boolean = false;

  public get hasFailedArtifacts(): boolean {
    return !!this.summary?.artifacts?.some(a => a.cloneStatus === 'Failed');
  }

  public isRetrying(id: number): boolean {
    return this.retryingArtifactIds.has(id) || this.retryingAll;
  }

  /**
   * Retries downloading and cloning a single artifact into internal BOF storage.
   * @param artifact Target document artifact to retry.
   */
  public retryClone(artifact: IContractVaultArtifact): void {
    if (!artifact || this.isRetrying(artifact.id)) return;

    this.retryingArtifactIds.add(artifact.id);

    this.vaultHttpService.retryCloneArtifact(artifact.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          this.retryingArtifactIds.delete(artifact.id);
          if (resp && resp.result && this.summary?.artifacts) {
            const idx = this.summary.artifacts.findIndex(a => a.id === artifact.id);
            if (idx >= 0) {
              this.summary.artifacts[idx] = resp.result;
            }
          }
        },
        error: (err) => {
          this.retryingArtifactIds.delete(artifact.id);
          artifact.cloneStatus = 'Failed';
          artifact.errorMessage = err?.message || 'Retry clone failed.';
        }
      });
  }

  /**
   * Retries cloning all failed artifacts for this proposal.
   */
  public retryAllFailed(): void {
    if (!this.proposalProfileId || this.retryingAll) return;

    this.retryingAll = true;

    this.vaultHttpService.retryAllArtifacts(this.proposalProfileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          this.retryingAll = false;
          if (resp && resp.result && this.summary) {
            this.summary.artifacts = resp.result;
          } else {
            this.loadVaultSummary();
          }
        },
        error: (err) => {
          this.retryingAll = false;
          this.loadVaultSummary();
        }
      });
  }

  public close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
}

